import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { TRBMaxRiskTableRow } from 'src/features/types/trbTypes';
import { SortField, SortOrder, sortMaxRiskTableData } from 'src/features/utils/trbUtils';

interface TRBMaxRiskTableProps {
    maxRiskTableData: TRBMaxRiskTableRow[];
}

const TRBMaxRiskTable: React.FC<TRBMaxRiskTableProps> = ({ maxRiskTableData }) => {
    const [currentShipOption, setCurrentShipOption] = useState('default');
    const [currentProcessPathGroup, setCurrentProcessPathGroup] = useState('');
    const [sortField, setSortField] = useState<SortField>('fc');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [searchText, setSearchText] = useState('');
    const [mobile, setMobile] = useState(window.innerWidth <= 800);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Listen for window resize
    useEffect(() => {
        const handleResize = () => setMobile(window.innerWidth <= 800);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Set initial process path group
    useEffect(() => {
        const processPathGroups = maxRiskTableData.map((row) => row.processPathGroup);
        const uniqueGroups = Array.from(new Set(processPathGroups));
        if (uniqueGroups.includes('AGGREGATE')) {
            setCurrentProcessPathGroup('AGGREGATE');
        } else {
            setCurrentProcessPathGroup('ALL');
        }
    }, [maxRiskTableData]);

    const uniqueShipOptions = useMemo(() => {
        const options = Array.from(new Set(maxRiskTableData.map((row) => row.shipOption)));
        options.push('all');
        return options;
    }, [maxRiskTableData]);

    const uniqueProcessPathGroups = useMemo(() => {
        const groups = Array.from(new Set(maxRiskTableData.map((row) => row.processPathGroup)));
        groups.push('ALL');
        return groups;
    }, [maxRiskTableData]);

    const filteredData = useMemo(() => {
        let data = maxRiskTableData.filter(
            (row) =>
                (row.shipOption === currentShipOption || currentShipOption === 'all') &&
                (row.processPathGroup === currentProcessPathGroup || currentProcessPathGroup === 'ALL'),
        );

        // Apply search filter
        if (searchText) {
            const lower = searchText.toLowerCase();
            data = data.filter(
                (row) =>
                    row.fc.toLowerCase().includes(lower) ||
                    row.cpt.toLowerCase().includes(lower) ||
                    row.risk.toLowerCase().includes(lower),
            );
        }

        return sortMaxRiskTableData(data, sortField, sortOrder);
    }, [maxRiskTableData, currentShipOption, currentProcessPathGroup, sortField, sortOrder, searchText]);

    // Get all rows for an FC (across all PPGs) for the expand view
    const getRowsForFC = useCallback((fc: string) => {
        return maxRiskTableData
            .filter((row) => row.fc === fc)
            .sort((a, b) => {
                if (a.processPathGroup === currentProcessPathGroup) return -1;
                if (b.processPathGroup === currentProcessPathGroup) return 1;
                return a.processPathGroup.localeCompare(b.processPathGroup);
            });
    }, [maxRiskTableData, currentProcessPathGroup]);

    const toggleRow = (fc: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(fc)) {
                next.delete(fc);
            } else {
                next.add(fc);
            }
            return next;
        });
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const renderSortIndicator = (field: SortField) => {
        if (sortField !== field) return null;
        return sortOrder === 'asc' ? ' ▲' : ' ▼';
    };

    return (
        <div className="trb-table-container">
            {/* Controls */}
            <div className="trb-table-controls">
                <div className="trb-dropdown">
                    <select
                        value={currentShipOption}
                        onChange={(e) => setCurrentShipOption(e.target.value)}
                        className="trb-select"
                    >
                        {uniqueShipOptions.map((option) => (
                            <option key={option} value={option}>
                                Ship Option: {option}
                            </option>
                        ))}
                    </select>
                </div>
                {currentProcessPathGroup !== 'AGGREGATE' && (
                    <div className="trb-dropdown">
                        <select
                            value={currentProcessPathGroup}
                            onChange={(e) => setCurrentProcessPathGroup(e.target.value)}
                            className="trb-select"
                        >
                            {uniqueProcessPathGroups.map((group) => (
                                <option key={group} value={group}>
                                    Process Path Group: {group}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="trb-search">
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="trb-search-input"
                    />
                </div>
            </div>

            {/* Table */}
            <table className="trb-table trb-table-striped">
                <thead>
                    <tr>
                        <th onClick={() => handleSort('fc')}>FC{renderSortIndicator('fc')}</th>
                        {!mobile && <th onClick={() => handleSort('cptUTC')}>CPT At Risk (UTC){renderSortIndicator('cptUTC')}</th>}
                        {!mobile && <th onClick={() => handleSort('cpt')}>CPT At Risk (FC&apos;s Local Time){renderSortIndicator('cpt')}</th>}
                        <th onClick={() => handleSort('timeToCPT')}>Time To High-Risk CPT{renderSortIndicator('timeToCPT')}</th>
                        {!mobile && <th onClick={() => handleSort('lowThreshold')}>Low Threshold{renderSortIndicator('lowThreshold')}</th>}
                        {!mobile && <th onClick={() => handleSort('highThreshold')}>High Threshold{renderSortIndicator('highThreshold')}</th>}
                        {!mobile && <th onClick={() => handleSort('highThresholdMinusRisk')}>High Threshold Minus Risk{renderSortIndicator('highThresholdMinusRisk')}</th>}
                        {!mobile && <th onClick={() => handleSort('risk')}>Current Risk{renderSortIndicator('risk')}</th>}
                        <th>Process Path Group</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredData.length === 0 ? (
                        <tr>
                            <td colSpan={mobile ? 3 : 9} className="trb-no-data">No data available</td>
                        </tr>
                    ) : (
                        filteredData.map((row, index) => (
                            <React.Fragment key={`${row.fc}-${row.shipOption}-${row.processPathGroup}`}>
                                <tr
                                    onClick={mobile ? () => toggleRow(row.fc) : undefined}
                                    className={mobile ? 'trb-expandable-row' : ''}
                                >
                                    <td>{row.fc}</td>
                                    {!mobile && <td>{row.cptUTC}</td>}
                                    {!mobile && <td>{row.cpt}</td>}
                                    <td>{row.timeToCPT}</td>
                                    {!mobile && <td>{row.lowThreshold}</td>}
                                    {!mobile && <td>{row.highThreshold}</td>}
                                    {!mobile && <td>{row.highThresholdMinusRisk}</td>}
                                    {!mobile && <td>{row.risk}</td>}
                                    <td>{row.processPathGroup}</td>
                                </tr>
                                {mobile && expandedRows.has(row.fc) && (
                                    <tr className="trb-expanded-row">
                                        <td colSpan={3}>
                                            <div className="trb-expand-content">
                                                {getRowsForFC(row.fc).map((ppgRow) => (
                                                    <div className="trb-ppg-group" key={ppgRow.processPathGroup}>
                                                        <h4>{ppgRow.processPathGroup}</h4>
                                                        <p><strong>CPT At Risk (FC&apos;s Local Time):</strong></p>
                                                        <p>{ppgRow.cpt}</p>
                                                        <p><strong>Time To High-Risk CPT:</strong></p>
                                                        <p>{ppgRow.timeToCPT}</p>
                                                        <p><strong>High Threshold Minus Risk:</strong> {ppgRow.highThresholdMinusRisk}</p>
                                                        <p><strong>Current Risk:</strong> {ppgRow.risk}</p>
                                                    </div>
                                                ))}
                                                <p className="trb-threshold-text">Low Threshold: {row.lowThreshold}</p>
                                                <p className="trb-threshold-text">High Threshold: {row.highThreshold}</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default TRBMaxRiskTable;
